import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { TEMPLATE_VAR_REGEX } from 'sprites-types/utils/template-variables'
import { WorkflowConfig } from 'sprites-types/workflow/core'

export interface RetryValidationResult {
  canRetryFromNode: boolean
  availableStages: Array<'tool_execution' | 'edge_selection'>
  reason?: string
}

export interface RetryValidationResults {
  [nodeExecutionId: string]: RetryValidationResult
}

/**
 * Build execution DAG showing dependencies between node executions
 */
function buildExecutionDAG(nodeExecutions: Record<string, NodeExecution>): {
  dependencies: Map<string, Set<string>>
  dependents: Map<string, Set<string>>
} {
  const dependencies = new Map<string, Set<string>>()
  const dependents = new Map<string, Set<string>>()

  // Initialize maps for all nodes
  for (const executionId of Object.keys(nodeExecutions)) {
    dependencies.set(executionId, new Set())
    dependents.set(executionId, new Set())
  }

  // Build dependencies by analyzing variable references in each execution
  for (const [executionId, execution] of Object.entries(nodeExecutions)) {
    // Parse inputs to find references to other node executions
    const inputStr = JSON.stringify(execution.inputs)
    const matches = inputStr.matchAll(TEMPLATE_VAR_REGEX)

    for (const match of matches) {
      const varPath = match[1]
      const parts = varPath.split('.')
      if (parts.length >= 1) {
        // Check if this references another node execution
        const referencedNodeId = parts[0]
        for (const [otherExecutionId, otherExecution] of Object.entries(nodeExecutions)) {
          if (otherExecution.nodeId === referencedNodeId && otherExecutionId !== executionId) {
            dependencies.get(executionId)?.add(otherExecutionId)
            dependents.get(otherExecutionId)?.add(executionId)
          }
        }
      }
    }
  }

  return { dependencies, dependents }
}

/**
 * Prune DAG by removing nodes with incompatible hashes and their descendants
 */
function pruneDAGByHashes(
  nodeExecutions: Record<string, NodeExecution>,
  validHashes: Set<string>,
  hashType: 'input' | 'output'
): {
  validExecutions: Record<string, NodeExecution>
  prunedExecutions: Record<string, NodeExecution>
} {
  const validExecutions: Record<string, NodeExecution> = {}
  const prunedExecutions: Record<string, NodeExecution> = {}

  // First pass: identify nodes with invalid hashes
  for (const [executionId, execution] of Object.entries(nodeExecutions)) {
    const hash = hashType === 'input' ? execution.inputHash : execution.outputHash
    if (!hash || validHashes.has(hash)) {
      validExecutions[executionId] = execution
    } else {
      prunedExecutions[executionId] = execution
    }
  }

  // Second pass: recursively remove descendants of pruned nodes
  const dag = buildExecutionDAG(nodeExecutions)
  const toRemoveSet = new Set<string>(Object.keys(prunedExecutions))

  function removeDescendants(executionId: string) {
    const descendants = dag.dependents.get(executionId) || new Set()
    for (const descendantId of descendants) {
      if (!toRemoveSet.has(descendantId)) {
        toRemoveSet.add(descendantId)
        // Move from valid to pruned
        if (validExecutions[descendantId]) {
          prunedExecutions[descendantId] = validExecutions[descendantId]
          delete validExecutions[descendantId]
        }
        removeDescendants(descendantId)
      }
    }
  }

  // Remove descendants for all initially pruned nodes
  for (const prunedId of Object.keys(prunedExecutions)) {
    removeDescendants(prunedId)
  }

  return { validExecutions, prunedExecutions }
}

/**
 * Smart two-step retry validation using input and output hash analysis
 * Step 1: Prune by input hashes (more stable) -> nodes that survive can use tool_execution
 * Step 2: Prune by output hashes -> subset that survives can also use edge_selection
 */
export function validateAllNodesForRetry(
  nodeExecutions: Record<string, NodeExecution>,
  targetWorkflow: WorkflowConfig
): RetryValidationResults {
  const results: RetryValidationResults = {}

  // Step 1: Extract valid input hashes from target workflow (more stable)
  const validInputHashes = new Set<string>()
  for (const node of targetWorkflow.nodes) {
    if (node.inputHash) {
      validInputHashes.add(node.inputHash)
    }
  }

  // Step 2: Extract valid output hashes from target workflow
  const validOutputHashes = new Set<string>()
  for (const node of targetWorkflow.nodes) {
    if (node.outputHash) {
      validOutputHashes.add(node.outputHash)
    }
  }

  // Step 3: First pruning by input hashes (more stable)
  const { validExecutions: inputValidExecutions } = pruneDAGByHashes(
    nodeExecutions,
    validInputHashes,
    'input'
  )

  // Step 4: Second pruning by output hashes on the input-valid subset
  const { validExecutions: outputValidExecutions } = pruneDAGByHashes(
    inputValidExecutions,
    validOutputHashes,
    'output'
  )

  // Step 5: Validate each node execution
  for (const [nodeExecutionId, execution] of Object.entries(nodeExecutions)) {
    const result: RetryValidationResult = {
      canRetryFromNode: false,
      availableStages: [],
    }

    // Basic checks first
    if (execution.status === 'running' || execution.status === 'pending') {
      result.reason = 'Cannot retry from running or pending nodes'
      results[nodeExecutionId] = result
      continue
    }

    if (execution.status === 'waiting_approval') {
      result.reason = 'Cannot retry from nodes waiting for approval'
      results[nodeExecutionId] = result
      continue
    }

    // Find the corresponding workflow node
    const targetNode = targetWorkflow.nodes.find((node) => node.id === execution.nodeId)
    if (!targetNode) {
      result.reason = 'Node not found in target workflow'
      results[nodeExecutionId] = result
      continue
    }

    // tool_execution: Available for nodes that survived input hash pruning
    if (inputValidExecutions[nodeExecutionId]) {
      if (execution.status === 'completed' || execution.status === 'failed') {
        result.availableStages.push('tool_execution')
      }
    }

    // edge_selection: Available for nodes that survived both input AND output hash pruning
    if (outputValidExecutions[nodeExecutionId]) {
      if (execution.status === 'completed' && execution.outputs) {
        result.availableStages.push('edge_selection')
      }
    }

    // Set overall result
    if (result.availableStages.length > 0) {
      result.canRetryFromNode = true
    } else {
      result.reason = 'Node hash is incompatible with target workflow version'
    }

    results[nodeExecutionId] = result
  }

  return results
}

/**
 * Helper to check if a specific node execution can be retried
 */
export function canRetryFromNode(
  nodeExecutionId: string,
  validationResults: RetryValidationResults
): boolean {
  return validationResults[nodeExecutionId]?.canRetryFromNode ?? false
}

/**
 * Helper to get available resume stages for a specific node execution
 */
export function getAvailableResumeStages(
  nodeExecutionId: string,
  validationResults: RetryValidationResults
): Array<'tool_execution' | 'edge_selection'> {
  return validationResults[nodeExecutionId]?.availableStages ?? []
}

/**
 * Helper to get the reason why a node cannot be retried
 */
export function getRetryFailureReason(
  nodeExecutionId: string,
  validationResults: RetryValidationResults
): string | undefined {
  return validationResults[nodeExecutionId]?.reason
}

/**
 * Extract workflow execution ID from node execution ID
 * Format: node_execution_<uuid>_<uuid> (where first uuid is workflow execution id)
 * Returns: workflow_execution_<uuid>
 */
export function extractExecutionIdFromNodeExecutionId(nodeExecutionId: string): string {
  if (!nodeExecutionId.startsWith('node_execution_')) {
    throw new Error(
      `Invalid node execution ID format: ${nodeExecutionId}. Expected format: node_execution_<uuid>_<uuid>`
    )
  }

  // Remove 'node_execution_' prefix
  const withoutPrefix = nodeExecutionId.slice('node_execution_'.length)

  // Find the last underscore to separate the workflow execution UUID from the node UUID
  const lastUnderscoreIndex = withoutPrefix.lastIndexOf('_')
  if (lastUnderscoreIndex === -1) {
    throw new Error(
      `Invalid node execution ID format: ${nodeExecutionId}. Expected format: node_execution_<uuid>_<uuid>`
    )
  }

  // Extract workflow execution UUID (everything before the last underscore)
  const workflowExecutionUuid = withoutPrefix.slice(0, lastUnderscoreIndex)

  // Return as workflow_execution_<uuid>
  return `workflow_execution_${workflowExecutionUuid}`
}
